"use client";
import styles from "./index.module.css";
import { useEffect, useMemo, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import Carousel from "../carousel/carousel";
import EmptyState from "../common/EmptyState";
import { useClient } from "../../lib/context/auth-context";
import { useQuery } from "@tanstack/react-query";
import {
  generateAbbreviation,
  formatCurrency,
  normalizeName,
  generateProductUrl,
} from "../../lib/util";
import { useProductPriceDisplay } from "./useProductPriceDisplay";
import Pagination from "../common/Pagination";
import PopUp from "./popUp";
import { useSwitchingOrgs } from "../organisation/useSwitchingOrgs";
import { useHistoryManager } from "../history/useHistoryManager";
import {
  setAccessPoint,
  setAccessPointId,
  setAccessPoints,
} from "../slice/accessOrg-slice";
import { useAppDispatch } from "../slice/hooks";

const SimilarProducts = ({
  product,
  setProduct,
  token,
  isSwitching,
  setSwitching,
  storePath,
  accessPoints,
  navLocation,
}) => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const client = useClient();

  const [searchkey] = useState(product?.name);
  const dispatch = useAppDispatch();

  const [error, setError] = useState(null);
  const [openProductId, setOpenProductId] = useState(null);
  const { navigateTo } = useHistoryManager(storePath);

  const [state, setState] = useState({
    products: [],
    limit: 12,
    total: 0,
    currentPage: 1,
    page: 1,
    search: searchkey,
  });

  const queryKey = useMemo(
    () => ["feeds", state.limit, state.page, state.search, product?.productId],
    [state.limit, state.page, state.search, product?.productId],
  );

  const {
    data: feeds,
    isLoading,
    isFetching,
    refetch,
    isRefetching,
  } = useQuery(
    queryKey,
    async () => {
      const params = [
        `limit=${state.limit}`,
        `page=${state.page}`,
        `searchTerm=${state.search}`,
      ];

      if (navLocation !== "marketplace") {
        params.push(`profileId=${product.host.profileId}`);
      }

      const res = await client(`product/products?${params.join("&")}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return res.data;
    },
    {
      enabled: Boolean(token && state.search),
      keepPreviousData: true,
      onError: (err) => {
        setError(err.message);
        console.error("Failed to fetch products:", err);
      },
    },
  );

  useEffect(() => {
    if (feeds && feeds.products) {
      const { products, limit, total, currentPage } = feeds;
      setState((prev) => {
        const all = [...(prev.products || []), ...products];
        const unique = Array.from(
          new Map(
            all
              .filter((p) => p.productId !== product?.productId)
              .map((p) => [p.productId, p]),
          ).values(),
        );
        return { ...prev, products: unique, limit, total, currentPage };
      });
    }
  }, [feeds]);

  useEffect(() => {
    const newSearch = product?.name || "";
    setState((prev) => {
      if (prev.search === newSearch) return prev;
      return {
        ...prev,
        search: newSearch,
        products: [],
        page: 1,
        limit: 12,
        total: 0,
        currentPage: 1,
      };
    });
  }, [product?.name]);

  const {
    startSwitch,
    isSwitching: hookSwitching,
    error: switchError,
  } = useSwitchingOrgs({ token });

  const loadingOrSwitching = Boolean(isLoading || hookSwitching || isSwitching);

  const handleProductClick = (productClicked) => {
    if (productClicked.host?.profileId === product.host?.profileId) {
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      setProduct(productClicked);
      const accessPointName = normalizeName(productClicked.accessPointName);
      const path = `store/${productClicked.host?.handle}/${accessPointName}`;
      const productPath = generateProductUrl(path, productClicked);
      const accessPoint = accessPoints.find(
        (ap) => ap.accessPointId === productClicked.accessPointId,
      );

      navigateTo(
        productPath,
        {
          urlData: {
            product: productClicked,
            accessPoint: accessPoint,
          },
          onNavigate: (navigate) => {
            if (navigate) {
              setSwitching(false);
              dispatch(setAccessPointId(productClicked.accessPointId));
              dispatch(setAccessPoint(accessPoint));
            }
          },
        },
        false,
      );
      return;
    }
    setSelectedProduct(productClicked);
    setOpenProductId(productClicked.productId);
  };

  // Called from PopUp 'Yes' action
  const yesClick = (productClicked) => {
    setSwitching?.(true);
    startSwitch(productClicked);
  };

  const openPage = (page) => {
    setState((prev) => ({ ...prev, page }));
  };

  const ProductPrice = ({
    product,
    accessPoint = null,
    recievedPriceOption = null,
  }) => {
    const { productPrice, getDisplayPrice } = useProductPriceDisplay(
      accessPoint,
      product,
      recievedPriceOption,
    );
    const amount =
      productPrice ?? getDisplayPrice(product) ?? product?.amount ?? 0;
    let formatted;
    try {
      formatted = formatCurrency(amount, product.currency);
    } catch (e) {
      formatted = `${amount}`;
    }
    return (
      <h4
        className={styles.priceLine}
        style={{ fontSize: "1.9rem", fontWeight: 500 }}
      >
        {formatted}
      </h4>
    );
  };

  // close popup when clicking outside
  useEffect(() => {
    const handleDocumentClick = (e) => {
      if (!openProductId) return;
      const openEl = document.querySelector(
        `[data-product-id="${openProductId}"]`,
      );
      if (openEl && openEl.contains(e.target)) return;
      setOpenProductId(null);
    };
    document.addEventListener("mousedown", handleDocumentClick);
    return () => document.removeEventListener("mousedown", handleDocumentClick);
  }, [openProductId]);

  if (
    state.products.length === 0 &&
    !loadingOrSwitching &&
    !error &&
    !isRefetching &&
    !isSwitching
  ) {
    return (
      <>
        {/* <div
          className={styles.similarProductList}
          style={{ marginBottom: "3rem" }}
        >
          <h2>Similar Product in other stores</h2>
        </div>
        <EmptyState>
          There are no similar products to this available in our other Stores,
          Check back later...
        </EmptyState> */}
      </>
    );
  }

  if (error || switchError) {
    return (
      <>
        <div
          className={styles.similarProductList}
          style={{ marginBottom: "2.5rem" }}
        >
          <h2>Similar Product in other stores</h2>
        </div>
        <EmptyState>{error || String(switchError)}</EmptyState>
      </>
    );
  }

  return (
    <>
      <div className={styles.similarContainer}>
        <div className={styles.similarProductList}>
          <h2>
            {loadingOrSwitching || isRefetching
              ? "Loading Similar Products ..."
              : "Shop for similar Products"}
          </h2>
        </div>
        <div className={styles.similarProducts}>
          {(isLoading || isFetching) && state.products.length === 0 && (
            <div className={styles.similarProducts}>
              {Array.from({ length: 9 }).map((_, idx) => (
                <div
                  key={idx}
                  style={{
                    width: "30rem",
                    padding: "1rem",
                    boxSizing: "border-box",
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                    alignItems: "stretch",
                  }}
                >
                  <div style={{ width: "100%" }}>
                    <Skeleton
                      height={"21rem"}
                      style={{ borderRadius: 8, display: "block" }}
                    />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.6rem",
                      marginTop: "0.5rem",
                    }}
                  >
                    <Skeleton height={12} width={"40%"} />
                    <Skeleton height={16} width={"60%"} />
                    <Skeleton height={18} width={"80%"} />
                  </div>
                </div>
              ))}
            </div>
          )}
          {(isSwitching || hookSwitching) && (
            <div className={styles.similarProducts}>
              {Array.from({ length: 9 }).map((_, idx) => (
                <div
                  key={idx}
                  style={{
                    width: "30rem",
                    padding: "1rem",
                    boxSizing: "border-box",
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                    alignItems: "stretch",
                  }}
                >
                  <div style={{ width: "100%" }}>
                    <Skeleton
                      height={"21rem"}
                      style={{ borderRadius: 8, display: "block" }}
                    />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.6rem",
                      marginTop: "0.5rem",
                    }}
                  >
                    <Skeleton height={12} width={"40%"} />
                    <Skeleton height={16} width={"60%"} />
                    <Skeleton height={18} width={"80%"} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loadingOrSwitching &&
            state.products.map((product) => {
              const hasMedia =
                Array.isArray(product.media) && product.media.length > 0;
              const media = product.media
                ?.map((m) => m)
                .filter(Boolean)
                .map((m) => ({
                  mediaUrl: m.mediaUrl,
                  mediaThumb: m.mediaThumb,
                  mediaName: m.mediaName,
                  mediaType: m.mediaType,
                  mediaExt: m.mediaExt,
                  mediaWidth: m.mediaWidth,
                  mediaHeight: m.mediaHeight,
                  mediaTime: m.mediaTime,
                  _id: m._id,
                }));

              return (
                <div
                  key={product.productId}
                  data-product-id={product.productId}
                  onClick={() => handleProductClick(product)}
                  className={styles.similarProductItem}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ")
                      handleProductClick(product);
                  }}
                >
                  {hasMedia ? (
                    <div style={{ paddingTop: "1rem", flex: 1 }}>
                      <Carousel
                        media={media}
                        enablePreview={false}
                        autoscroll={true}
                        showIndicator={false}
                        hideThumb={true}
                        customHeight={"20rem"}
                      />
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flex: 1,
                        padding: "2rem 0",
                      }}
                    >
                      <div
                        style={{
                          width: "95%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "#f0f0f0",
                          padding: "1rem",
                          boxSizing: "border-box",
                        }}
                        aria-hidden
                      >
                        <div
                          style={{
                            fontSize: "3.5rem",
                            color: "#444",
                            textTransform: "uppercase",
                            fontWeight: 700,
                          }}
                        >
                          {generateAbbreviation(
                            product.name || product.productId,
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className={styles.itemInfo}>
                    <h4>
                      <p className={styles.names}>{product.name}</p>
                    </h4>

                    <ProductPrice
                      product={product}
                      accessPoint={null}
                      recievedPriceOption={product?.storeFrontDisplayPrice}
                    />

                    <h4>
                      <span>Sold by</span>{" "}
                      <p className={styles.names}>{product.host.name}</p>
                    </h4>
                  </div>

                  <PopUp
                    product={product}
                    openProductId={openProductId}
                    setOpenProductId={setOpenProductId}
                    yesClick={yesClick}
                  />
                </div>
              );
            })}
        </div>
        {(!isSwitching || !hookSwitching) && (
          <Pagination
            infiniteScroll
            total={state.total}
            limit={state.limit}
            page={state.page}
            onPageClick={openPage}
            enableManualMode
            autoLoadThreshold={5}
            isLoading={isLoading}
            isFetching={isFetching}
            showEndOfList
            endOfListText="🎉 You've reached the end!"
          />
        )}
      </div>
    </>
  );
};

export default SimilarProducts;
